import { Suspense, forwardRef, lazy, useMemo } from 'react';
import { Route, Switch } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';

import MainLayout from 'layouts/MainLayout';
import PrivateRoute from './PrivatRouter';
import CoreRouter from './CoreRouter';
import LanguageRoute, { lang_route } from './LanguageRoute';
export { default as usePath } from './usePath.hook';

const AboutPage = lazy(() => import('pages/About'));
const BlogArticlePage = lazy(() => import('pages/BlogArticlePage'));
const BlogSearchPage = lazy(() => import('pages/BlogSearch'));
const BlogsPage = lazy(() => import('pages/BlogsPage'));
const CareerApplyForm = lazy(() => import('pages/CareerApplyForm'));
const CareerArticle = lazy(() => import('pages/CareerArticle'));
const CareerPage = lazy(() => import('pages/Career'));
const CareerPageList = lazy(() => import('pages/CareerList'));
const CartPage = lazy(() => import('pages/CartPage'));
const Checkout = lazy(() => import('pages/Checkout'));
const CurationPage = lazy(() => import('pages/Curation'));
const ExpiredPasswordPage = lazy(() => import('pages/ExpiredPasswordPage'));
const FaqsPage = lazy(() => import('pages/FaqsPage'));
const ForgotPasswordPage = lazy(() => import('pages/ForgotPasswordPage'));
const GrantsFunds = lazy(() => import('pages/ProfileAccount/GrantsFunds'));
const GreenWingRequest = lazy(() => import('pages/GreenWingRequest'));
const HelpCenterArticlePage = lazy(() => import('pages/HelpCenterArticle'));
const HelpCenterPage = lazy(() => import('pages/HelpCenter'));
const HelpCenterSearchPage = lazy(() => import('pages/HelpCenterSearch'));
const HomePage = lazy(() => import('pages/HomePage'));
const InviteAgree = lazy(() => import('pages/ProfileLabGroups/InviteAgree'));
const MakeSuggestionPage = lazy(() => import('pages/MakeSuggestion'));
const MarketplacePage = lazy(() => import('pages/Marketplace'));
const News = lazy(() => import('pages/News'));
const Newsroom = lazy(() => import('pages/Newsroom'));
const NewSupplierFormPage = lazy(() => import('pages/NewSupplierFormPage'));
const NotFoundPage = lazy(() => import('pages/NotFoundPage'));
const OrderDetailsPage = lazy(() => import('pages/OrderDetailsPage'));
const PaymentOptions = lazy(() =>
  import('pages/ProfileAccount/PaymentOptions'),
);
const Pricing = lazy(() => import('pages/Pricing'));
const PrivacyPolicyPage = lazy(() => import('pages/PrivacyPolicyPage'));
const ProductPage = lazy(() => import('pages/ProductPage'));
const ProductRedirect = lazy(() => import('pages/ProductRedirect'));
const ProductsListPage = lazy(() => import('pages/ProductsListPage'));
const ProfileAccountAddressBook = lazy(() =>
  import('pages/ProfileAccount/AddressBook'),
);
const ProfileAccountPersonalInformation = lazy(() =>
  import('pages/ProfileAccount/PersonalInformation'),
);
const ProfileDashboard = lazy(() => import('pages/ProfileDashboard'));
const ProfileLabGroups = lazy(() => import('pages/ProfileLabGroups'));
const ProfileLogin = lazy(() => import('pages/ProfileLogin'));
const ProfileOrders = lazy(() => import('pages/ProfileOrders'));
const ProfileRequestsQuote = lazy(() =>
  import('pages/ProfileRequests/RequestsQuote'),
);
const ProfileRequestsSupport = lazy(() =>
  import('pages/ProfileRequests/RequestsSupport'),
);
const ProfileVerificationDocuments = lazy(() =>
  import('pages/ProfileAccount/VerificationDocuments'),
);
const Punchout = lazy(() => import('pages/Punchout'));
const ReportBugPage = lazy(() => import('pages/ReportBug'));
const RequestPreferredPricingPage = lazy(() =>
  import('pages/RequestPreferredPricing'),
);
const RequestSupplierAccount = lazy(() =>
  import('pages/RequestSupplierAccount'),
);
const ScheduleDemoPage = lazy(() => import('pages/ScheduleDemo'));
const ScienceSamplesPage = lazy(() => import('pages/ScienceSamplesPage'));
const SharedCartPage = lazy(() => import('pages/SharedCart'));
const SignIn = lazy(() => import('pages/SignInPage'));
const SignInVerificationPage = lazy(() =>
  import('pages/SignInVerificationPage'),
);
const SignUp = lazy(() => import('pages/SignUpPage'));
const SupervisePage = lazy(() => import('pages/SupervisePage'));
const SupplierPartnersPage = lazy(() => import('pages/SupplierPartnersPage'));
const TermsAndConditionsOfSalePage = lazy(() =>
  import('pages/TermsAndConditionsOfSalePage'),
);
const TermsOfUsePage = lazy(() => import('pages/TermsOfUsePage'));

const routes_list = [
  // LANDINGS
  ...[
    {
      path: '/',
      component: HomePage,
    },
    {
      path: '/terms-of-use',
      component: TermsOfUsePage,
    },
    {
      path: '/privacy-policy',
      component: PrivacyPolicyPage,
    },
    {
      path: '/terms-and-conditions-of-sale',
      component: TermsAndConditionsOfSalePage,
    },
    {
      path: '/faqs',
      component: FaqsPage,
    },
    {
      path: '/supplier-partners',
      component: SupplierPartnersPage,
    },
    {
      path: '/schedule-demo',
      component: ScheduleDemoPage,
    },
    {
      path: '/report-bug',
      component: ReportBugPage,
    },
    {
      path: '/make-suggestion',
      component: MakeSuggestionPage,
    },
    {
      path: '/marketplace',
      component: MarketplacePage,
    },
    {
      path: '/shared-cart',
      component: SharedCartPage,
    },
    {
      path: '/help-center',
      component: HelpCenterPage,
    },
    {
      path: '/help-center-search',
      component: HelpCenterSearchPage,
    },
    {
      path: '/help-center/:slug',
      component: HelpCenterArticlePage,
    },
    {
      path: '/newsroom',
      component: Newsroom,
    },
    {
      path: '/newsroom/:id',
      component: News,
    },
    {
      path: '/science-samples',
      component: ScienceSamplesPage,
    },
    {
      path: '/about',
      component: AboutPage,
    },
    {
      path: '/open-positions',
      component: CareerPage,
    },
    {
      path: '/open-positions/list',
      component: CareerPageList,
    },
    {
      path: '/open-positions/list/:slug',
      component: CareerArticle,
    },
    {
      path: '/open-positions/list/:slug/apply',
      component: CareerApplyForm,
    },
    {
      path: '/curator',
      component: CurationPage,
    },
    {
      path: '/pricing',
      component: Pricing,
    },
    {
      path: '/request-supplier-account',
      component: RequestSupplierAccount,
    },
    {
      path: '/blogs',
      component: BlogsPage,
    },
    {
      path: '/blogs/:slug',
      component: BlogArticlePage,
    },
    {
      path: '/blog-search',
      component: BlogSearchPage,
    },
    {
      privat: true,
      path: '/request-preferred-pricing',
      component: RequestPreferredPricingPage,
    },
  ],

  // AUTH
  ...[
    {
      path: '/sign-in',
      component: SignIn,
    },
    {
      path: '/forgot-password',
      component: ForgotPasswordPage,
    },
    {
      path: '/expired-password/:email',
      component: ExpiredPasswordPage,
    },
    {
      path: '/supervise/:token',
      component: SupervisePage,
    },
    {
      path: '/email-verification',
      component: SignInVerificationPage,
    },
    {
      path: '/sign-up',
      component: SignUp,
    },
    {
      path: '/supplier-setup-form/:form_id',
      component: NewSupplierFormPage,
    },
    {
      path: '/punchout',
      component: Punchout,
    },
    {
      path: '/greenwing-request',
      component: GreenWingRequest,
    },
  ],

  // CART
  ...[
    {
      privat: true,
      path: '/cart',
      component: CartPage,
    },
    {
      exact: false,
      privat: true,
      path: '/checkout',
      component: Checkout,
    },
  ],

  // PROFILE
  ...[
    {
      privat: true,
      path: '/profile/dashboard',
      component: ProfileDashboard,
    },
    {
      privat: true,
      path: '/profile/account/personal',
      component: ProfileAccountPersonalInformation,
    },
    {
      privat: true,
      path: '/profile/account/documents',
      component: ProfileVerificationDocuments,
    },
    {
      privat: true,
      path: '/profile/account/address',
      component: ProfileAccountAddressBook,
    },
    {
      privat: true,
      path: '/profile/account/payment',
      component: PaymentOptions,
    },
    {
      privat: true,
      path: '/profile/account/grants',
      component: GrantsFunds,
    },
    {
      privat: true,
      path: '/profile/lab',
      component: ProfileLabGroups,
    },
    {
      path: '/profile/lab-invite/:token',
      component: InviteAgree,
    },
    {
      privat: true,
      path: '/profile/orders',
      component: ProfileOrders,
    },
    {
      privat: true,
      path: '/profile/orders/details/:order_id',
      component: OrderDetailsPage,
    },
    {
      privat: true,
      path: '/profile/login-security',
      component: ProfileLogin,
    },
    {
      privat: true,
      path: '/profile/requests/quote',
      component: ProfileRequestsQuote,
    },
    {
      privat: true,
      path: '/profile/requests/support',
      component: ProfileRequestsSupport,
    },
  ],

  // PRODUCTS
  ...[
    {
      privat: true,
      path: '/products',
      component: ProductsListPage,
    },
    {
      path: '/product/:slug',
      component: ProductRedirect,
    },
    {
      path: '/product/:supplier_code/:brand_slug/:slug',
      component: ProductPage,
    },
  ],
];

const LoadingComponent = () => {
  return (
    <MainLayout pending>
      <div />
    </MainLayout>
  );
};

const makeLazyComponent = (Component) => {
  return forwardRef((props, ref) => {
    return (
      <ErrorBoundary fallback={null}>
        <Suspense fallback={<LoadingComponent />}>
          <Component {...props} ref={ref} />
        </Suspense>
      </ErrorBoundary>
    );
  });
};

const CustomRoute = (props) => {
  const { privat, component, ...router } = props;
  const RouteComponent = privat ? PrivateRoute : Route;

  const Component = useMemo(() => {
    return makeLazyComponent(component);
  }, [component]);

  return <RouteComponent {...router} component={Component} />;
};

const Routes = () => {
  return (
    <CoreRouter>
      <LanguageRoute>
        <Switch>
          {routes_list.map((route) => {
            const { path, exact = true, component, privat } = route;
            const fullPath = [lang_route, path].join('');

            return (
              <CustomRoute
                key={path}
                exact={exact}
                path={fullPath}
                privat={privat}
                component={component}
              />
            );
          })}

          <CustomRoute path="*" exact component={NotFoundPage} />
        </Switch>
      </LanguageRoute>
    </CoreRouter>
  );
};

export default Routes;
